// GENERATED — DO NOT HAND-EDIT.
//
// Source of truth: specs/tier-05-experience/message.entity-hint.yaml
// Generator: tools/v2-gen/v2gen.py
// Regenerate via: make v2-generate-screens
//
// Hand-edits to non-override-block content WILL be lost on next
// regeneration. See specs/_product/dashboard-v2-page-generator.md §5
// for override blocks (imports / helpers / actions / custom-tabs).

import type { DetailPageConfig } from "../../components/composition/DetailPage";
import { messagesApi } from "../../api/messages";
import type { Message } from "../../api/types";
// override-block:imports BEGIN
import { createElement } from "react";
import type { MessageStatus, MessageKind } from "../../api/types";
// CHG-1294 — per-page RBAC gate wiring (consumes GATE_CATALOG).
import { GATE_CATALOG } from "../../auth/gateCatalog";
import { toGate } from "../../auth/toGateProp";
// CHG-1325 — Tag taxonomy phase 3: Tags tab via EntityTagsSection.
import { EntityTagsSection } from "../../components/composition/EntityTagsSection";
// override-block:imports END

// override-block:helpers BEGIN
const STATUS_LABEL: Record<MessageStatus, string> = {
  draft: "Draft",
  scheduled: "Scheduled",
  sending: "Sending",
  sent: "Sent",
  partial: "Partial",
  failed: "Failed",
  expired: "Expired",
  cancelled: "Cancelled",
};

const KIND_LABEL: Record<MessageKind, string> = {
  live_session: "Live session",
  quick: "Quick send",
  standard: "Standard",
  campaign_arc: "Campaign arc",
  seasonal: "Seasonal",
  social_post: "Social post",
};

// Status → tone token. Active dispatch states (sending) + positive
// terminal (sent) lean giving (positive admin signal). Pre-dispatch
// (draft / scheduled) leans home (neutral / pending). Partial-success
// leans events (warning/orange). Hard failures (failed / expired /
// cancelled) lean people (muted concern — same access-state semantics
// as CHG-1249 STATUS_TONE for locked/disabled).
const STATUS_TONE: Record<MessageStatus, "g-giving" | "g-events" | "g-people" | "g-home"> = {
  draft: "g-home",
  scheduled: "g-home",
  sending: "g-giving",
  sent: "g-giving",
  partial: "g-events",
  failed: "g-people",
  expired: "g-people",
  cancelled: "g-people",
};
// override-block:helpers END

export const messageDetailConfig: DetailPageConfig<Message> = {
  idParam: "messageId",
  queryKey: (id: string) => ["messages", "detail", id] as const,
  queryFn: (id: string) => messagesApi.getMessage(id),
  backTo: "/messages",
  // override-block:title BEGIN
  title: (r) => r.intent_title,
  // override-block:title END
  // override-block:subtitle BEGIN
  // INTENTIONAL DEVIATION from entity-hint subtitle_field (created_at).
  // subtitle gets only the record (no formatters); ISO timestamps look
  // like garbage in header chrome. Project KIND_LABEL[m.kind] for a
  // meaningful sub-line. Formatted scheduled date appears in the
  // Scheduled status card. Pass-3 lesson from CHG-1254 + CHG-1235.
  subtitle: (r) => KIND_LABEL[r.kind] ?? r.kind,
  // override-block:subtitle END
  statusCards: (r, _fmt) => [
    // override-block:statcard-status BEGIN
    {
      id: "status",
      label: "Status",
      value: STATUS_LABEL[r.status] ?? r.status,
      format: "text",
      tone: STATUS_TONE[r.status] ?? "g-events",
    },
    // override-block:statcard-status END
    // override-block:statcard-kind BEGIN
    {
      id: "kind",
      label: "Kind",
      value: KIND_LABEL[r.kind] ?? r.kind,
      format: "text",
      tone: "g-home",
    },
    // override-block:statcard-kind END
    // override-block:statcard-tone BEGIN
    {
      id: "tone",
      label: "Tone",
      value: r.intent_tone ?? "—",
      format: "text",
      tone: "g-mygiving",
    },
    // override-block:statcard-tone END
    // override-block:statcard-scheduled BEGIN
    // INTENTIONAL DEVIATION: entity-hint declares relative-date format
    // but StatCardFormat union exposes "text" + render-slot. We use
    // format: "text" + explicit createElement render so a null
    // scheduled_at maps to "Not scheduled" cleanly while still showing
    // fmt.formatDate(..., "relative") when populated.
    {
      id: "scheduled",
      label: "Scheduled",
      value: r.scheduled_at ?? "—",
      format: "text",
      tone: "g-giving",
      render: createElement(
        "span",
        { className: "tabular" },
        r.scheduled_at
          ? _fmt.formatDate(r.scheduled_at, "relative")
          : "Not scheduled",
      ),
    },
    // override-block:statcard-scheduled END
  ],
  actions: (_r) => [
    // override-block:actions BEGIN
    // header_actions per entity-hint.pages.detail_v2.header_actions. All
    // forward-promised — message-edit form / publishMessage /
    // cancelMessage commands aren't wired into v2 yet.
    { id: "edit", label: "Edit", variant: "secondary", permissionGate: toGate(GATE_CATALOG.message_detail["edit"], "disable") },
    { id: "publish", label: "Publish", variant: "primary", permissionGate: toGate(GATE_CATALOG.message_detail["publish"], "hide") },
    { id: "cancel", label: "Cancel", variant: "ghost", permissionGate: toGate(GATE_CATALOG.message_detail["cancel"], "disable") },
    // override-block:actions END
  ],
  tabs: [
    // override-block:custom-tabs BEGIN
    // 5 tabs per entity-hint.pages.detail_v2.tabs + CHG-1325 Tags:
    //   - Content    — derived from primary record
    //   - Recipients — forward-promised; bridges to canonical message_deliveries (CONV-C5.1.A)
    //   - Audit      — forward-promised
    //   - Related donations — forward-promised
    //   - Tags       — CHG-1325 phase 3 EntityTagsSection
    {
      id: "content",
      label: "Content",
      render: (m, fmt) =>
        createElement(
          "div",
          { className: "card", style: { padding: 16, fontSize: 13, lineHeight: 1.6 } },
          createElement("div", null, "Subject: ", m.intent_title),
          createElement(
            "div",
            { style: { whiteSpace: "pre-wrap" } },
            "Body: ",
            m.intent_body ?? "—",
          ),
          createElement("div", null, "Tone: ", m.intent_tone ?? "—"),
          createElement("div", null, "Channel plan: ", m.channel_plan ?? "—"),
          createElement("div", null, "Trigger: ", m.trigger_kind),
          m.scheduled_at
            ? createElement(
                "div",
                null,
                "Scheduled for: ",
                fmt.formatDate(m.scheduled_at, "medium"),
              )
            : null,
          m.recurring_rule
            ? createElement("div", null, "Recurring (RRULE): ", m.recurring_rule)
            : null,
          m.event_type
            ? createElement("div", null, "Event trigger: ", m.event_type)
            : null,
          createElement(
            "div",
            null,
            "Created: ",
            fmt.formatDate(m.created_at, "medium"),
          ),
        ),
    },
    {
      id: "recipients",
      label: "Recipients",
      render: (_m) =>
        createElement(
          "div",
          {
            className: "card",
            style: { padding: 16, fontSize: 13, color: "var(--color-text-muted)" },
          },
          "No delivery data yet. (Coming soon: GET /messages/{id}/deliveries from the canonical message_deliveries table per CHG-1237 intent-vs-delivery distinction; CONV-C5.1.A consolidated the outbox here.)",
        ),
    },
    {
      id: "audit",
      label: "Audit",
      render: (_m) =>
        createElement(
          "div",
          {
            className: "card",
            style: { padding: 16, fontSize: 13, color: "var(--color-text-muted)" },
          },
          "No audit entries recorded yet. (Coming soon: GET /messages/{id}/audit.)",
        ),
    },
    {
      id: "related_donations",
      label: "Related donations",
      render: (_m) =>
        createElement(
          "div",
          {
            className: "card",
            style: { padding: 16, fontSize: 13, color: "var(--color-text-muted)" },
          },
          "No attributed donations yet. (Coming soon: GET /messages/{id}/related-donations.)",
        ),
    },
    // CHG-1325 — Tag taxonomy phase 3. Tags applied to this message.
    {
      id: "tags",
      label: "Tags",
      render: (m) =>
        createElement(EntityTagsSection, {
          entityType: "message",
          entityId: m.id,
        }),
    },
    // override-block:custom-tabs END
  ],
};
